import React, {useCallback, useEffect, useState} from 'react';
import { Grid, useMediaQuery, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import {useDispatch, useSelector} from 'react-redux';

import { StudioControlStyles } from '../styles';
import MainStudioActions from '../actions/MainStudioActions';
import {
  selectAudioId,
  selectCameraError,
  selectCameraId,
  selectCameraReady,
  selectIsOpenSettingsPanel, selectIsRunningAudioService
} from '../../../store/modules/studio/selectors';

import CameraSettingsPanelDesktop from './CameraSettingsPanelDesktop';
import CameraSettingsPanelMobile from './CameraSettingsPanelMobile';
import {detectIOS} from "../../../utility/helpers";
import {setAudioId, setCameraId, setRunningAudioService} from "../../../store/modules/studio/actions";
import StudioNoCamerasModal from "../modal/StudioNoCamerasModal";

type Props = {
  bottomViewContainerId: string;
};

const CameraSettingsPanel = (
  { bottomViewContainerId }: Props,
) => {
  const classes = StudioControlStyles();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('xs'));

  const isOpenSettingsPanel = useSelector(selectIsOpenSettingsPanel);


  const dispatch = useDispatch();
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const cameraId = useSelector(selectCameraId);
  const audioId = useSelector(selectAudioId);
  const cameraReady = useSelector(selectCameraReady);
  const cameraError = useSelector(selectCameraError);
  const [noCamerasModalOpen, setNoCamerasModalOpen] = useState<boolean>(false);
  const isRunning = useSelector(selectIsRunningAudioService);

  const openNoCamerasModal = useCallback(() => setNoCamerasModalOpen(true), [setNoCamerasModalOpen]);
  const closeNoCamerasModal = useCallback(() => setNoCamerasModalOpen(false), [setNoCamerasModalOpen]);
  /* eslint-disable react-hooks/exhaustive-deps */
  const setMediaDevices = useCallback(() => {

    if (!navigator.mediaDevices || (detectIOS() && process.env.REACT_APP_BUILD_ENV !== 'development')) {
      openNoCamerasModal();
      setVideoDevices([]);
      dispatch(setCameraId(''));
      dispatch(setAudioId(''));
      dispatch(setRunningAudioService(isRunning));
    } else {
      navigator.mediaDevices.enumerateDevices()
        .then((devices) => {
          const videoDevicesInfo: MediaDeviceInfo[] = [];
          const audioDevicesInfo: MediaDeviceInfo[] = [];
          devices.forEach((item) => {
            if (item.kind === 'videoinput') {
              videoDevicesInfo.push(item);
            }
            if(item.kind === 'audioinput' && item.deviceId !== 'default'){
              audioDevicesInfo.push(item);
            }
          });
          setVideoDevices(videoDevicesInfo);
          setAudioDevices(audioDevicesInfo);
          if (videoDevicesInfo.length) {
            if(!cameraId){
              dispatch(setCameraId(videoDevicesInfo[0].deviceId));
              dispatch(setAudioId(audioDevicesInfo[0].deviceId));
            }else{
              dispatch(setCameraId(''));
              dispatch(setCameraId(cameraId));
              dispatch(setAudioId(audioId))
            }
            dispatch(setRunningAudioService(isRunning));
            closeNoCamerasModal();
          } else {
            dispatch(setRunningAudioService(false));
            openNoCamerasModal();
            setVideoDevices([]);
            setAudioDevices([]);
          }
        })
        .catch((e)=>{
          openNoCamerasModal();
          setVideoDevices([]);
          setAudioDevices([]);
          console.error(e);
        })
    }
  }, [dispatch, openNoCamerasModal, closeNoCamerasModal, openNoCamerasModal]);

  useEffect(() => {
    if(cameraReady){
      try {
        navigator.mediaDevices.ondevicechange = () => setMediaDevices();
        setMediaDevices();
      } catch (e) {
        dispatch(setCameraId(''));
        dispatch(setAudioId(''));
        openNoCamerasModal();
        setVideoDevices([]);
        console.error(e);
      }
    }
  }, [dispatch, cameraReady]);

  useEffect(()=>{
    if(cameraError){
      try {
        navigator.mediaDevices.ondevicechange = () => setMediaDevices();
        setMediaDevices();
      } catch (e) {
        dispatch(setCameraId(''));
        dispatch(setAudioId(''));
        console.error(e);
      }
    }
  }, [cameraError]);

  useEffect(() => {
    if(videoDevices.length && !videoDevices.some((device) => device.deviceId === cameraId)){
      dispatch(setCameraId(videoDevices[0].deviceId));
    }
  }, [cameraId, videoDevices]);

  useEffect(() => {
    if(audioDevices.length && !audioDevices.some((device) => device.deviceId === audioId)){
      dispatch(setAudioId(audioDevices[0].deviceId));
    }
  }, [audioId, audioDevices]);

  return (
    <>
      <StudioNoCamerasModal open={noCamerasModalOpen} closeHandler={closeNoCamerasModal} />
      <div
        hidden={!isOpenSettingsPanel}
        className={clsx(
          isOpenSettingsPanel ? classes.panelSettingsContainer : '',
        )}
      >
        <Grid item container className={classes.panelSettings} spacing={3} alignItems="center">
          {compact
            ? (
              <CameraSettingsPanelMobile
                bottomViewContainerId={bottomViewContainerId}
                videoDevices={videoDevices}
                audioDevices={audioDevices}
              />
            ) : (
              <CameraSettingsPanelDesktop
                bottomViewContainerId={bottomViewContainerId}
                audioDevices={audioDevices}
                videoDevices={videoDevices}
              />
            )}
        </Grid>
        <MainStudioActions />
      </div>
    </>
  );
};

export default CameraSettingsPanel;
